/* eslint-disable */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  message,
  Table,
  Tag,
  Button,
  Select,
  Card,
  Modal,
  Alert,
  Descriptions,
  Divider,
  Input,
} from "antd";
import {
  ReloadOutlined,
  ThunderboltOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CreditCardOutlined,
} from "@ant-design/icons";
import Header from "../layouts/Header";
import Sidebar from "../layouts/Sidebar";
import { paymentsApi } from "../../apis";
import { hasPermission } from "../../utils/permissions";
import ResponsiveTable from "../../_components/ResponsiveTable";

const { Option } = Select;

const STATUS_COLORS = {
  success: "green",
  failed: "red",
  pending: "orange",
  refunded: "volcano",
};
const STATUS_ICONS = {
  success: <CheckCircleOutlined />,
  pending: <ClockCircleOutlined />,
};

// Helper function to get user role and permissions
const getUserPermissions = () => {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    return {
      role: user?.role,
      permissions: user?.permissions || [],
      userId: user?.id,
      isAdmin:
        (typeof user?.role === "string"
          ? user.role
          : user?.role?.name || ""
        ).toLowerCase() === "admin",
    };
  } catch {
    return { role: null, permissions: [], userId: null, isAdmin: false };
  }
};

// ══════════════════════════════════════════════════════════════════════════════
const PaymentsPage = () => {
  const history = useHistory();
  const location = useLocation();

  const [menu, setMenu] = useState(false);
  const [payments, setPayments] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    page: 1,
    limit: 15,
    status: undefined,
  });
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);
  const [simLoading, setSimLoading] = useState(null); // paymentId being simulated
  // Pay-now modal (triggered from query param ?reservationId=...)
  const [payModal, setPayModal] = useState({
    open: false,
    reservationId: null,
  });
  const [payMethod, setPayMethod] = useState("card");
  const [payLoading, setPayLoading] = useState(false);

  // Get user permissions
  const userData = getUserPermissions();
  const userPermissions = userData.permissions;
  const isAdmin = userData.isAdmin;

  // Check permissions
  const canViewPayments = hasPermission(userPermissions, "payments.view");
  const canManagePayments = hasPermission(userPermissions, "payments.manage");
  const canCreatePayments = hasPermission(
    userPermissions,
    "reservations.create",
  ); // For initiating payments

  // If the page was opened with ?reservationId=xxx, open the pay modal immediately
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const rid = params.get("reservationId");
    if (rid && canCreatePayments) {
      setPayModal({ open: true, reservationId: rid });
    }
  }, [canCreatePayments]);

  useEffect(() => {
    if (canViewPayments) {
      fetchPayments();
    }
  }, [filters, debouncedSearch, canViewPayments]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      console.log("💳 Fetching payments with query:", {
        page: filters.page,
        limit: filters.limit,
        status: filters.status,
        search: debouncedSearch,
      });
      
      const q = { 
        page: filters.page, 
        limit: filters.limit,
        ...(filters.status && canManagePayments ? { status: filters.status } : {}),
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      };
      
      const r = await paymentsApi.getAll(q);
      console.log("📊 Payments API Response:", {
        total: r.total,
        dataLength: (r.data || []).length,
        hasSearch: !!debouncedSearch,
      });
      
      let allPayments = r.data || [];
      
      // Client-side fallback filtering
      if (debouncedSearch) {
        const searchLower = debouncedSearch.toLowerCase();
        allPayments = allPayments.filter(payment => 
          payment.id?.toLowerCase().includes(searchLower) ||
          payment.reservation?.user?.firstName?.toLowerCase().includes(searchLower) ||
          payment.reservation?.user?.lastName?.toLowerCase().includes(searchLower) ||
          payment.reservation?.tripDate?.trip?.name?.toLowerCase().includes(searchLower) ||
          payment.transactionId?.toLowerCase().includes(searchLower)
        );
        console.log(`🔍 Client-side filtered payments from ${r.data?.length || 0} to ${allPayments.length}`);
      }
      
      setPayments(allPayments);
      
      // Adjust total for client-side filtering
      let adjustedTotal = r.total || allPayments.length;
      if (debouncedSearch) {
        adjustedTotal = allPayments.length;
      }
      setTotal(adjustedTotal);
      
      setSearching(false);
    } catch (error) {
      console.error("❌ Error fetching payments:", error.response?.data || error.message);
      if (error.response?.status === 403) {
        message.error("You don't have permission to view payments");
      } else {
        const errorMsg = error.response?.data?.message || error.message || "Failed to load payments";
        message.error(`Payments error: ${errorMsg}`);
      }
      setSearching(false);
    } finally {
      setLoading(false);
    }
  };

  // ── Simulate success (admin dev tool) ────────────────────────────────────
  const handleSimulate = async (paymentId) => {
    if (!canManagePayments) {
      message.error("You don't have permission to manage payments");
      return;
    }

    try {
      setSimLoading(paymentId);
      const r = await paymentsApi.simulateSuccess(paymentId);
      message.success(r.message || "Payment confirmed — ticket issued!");
      fetchPayments();
    } catch (e) {
      if (e.response?.status === 403) {
        message.error("You don't have permission to simulate payments");
      } else {
        message.error(e.response?.data?.message || "Simulation failed");
      }
    } finally {
      setSimLoading(null);
    }
  };

  // ── Pay Now (customer / from reservation detail) ─────────────────────────
  const handlePayNow = async () => {
    if (!canCreatePayments) {
      message.error("You don't have permission to initiate payments");
      setPayModal({ open: false, reservationId: null });
      return;
    }

    try {
      setPayLoading(true);
      const result = await paymentsApi.initiate({
        reservationId: payModal.reservationId,
        method: payMethod,
      });
      setPayModal({ open: false, reservationId: null });
      // Show the mock gateway info + simulate button for dev
      Modal.confirm({
        title: "Payment Initiated",
        icon: <CheckCircleOutlined style={{ color: "#7B4D12" }} />,
        content: (
          <div>
            <p style={{ marginBottom: 8 }}>
              Payment ID:{" "}
              <code
                style={{
                  background: "#f0f4ff",
                  padding: "2px 6px",
                  borderRadius: 4,
                  color: "#7B4D12",
                }}
              >
                {result.paymentId}
              </code>
            </p>
            <p style={{ marginBottom: 8 }}>
              Amount:{" "}
              <strong>EGP {Number(result.amount).toLocaleString()}</strong>
            </p>
            <p style={{ fontSize: 12, color: "#9ca3af" }}>
              In production this would redirect to the payment gateway. For
              testing, click "Simulate Success" to confirm the payment
              instantly.
            </p>
          </div>
        ),
        okText: canManagePayments ? "Simulate Success (Dev)" : "Close",
        cancelText: "Close",
        onOk: canManagePayments
          ? () => handleSimulate(result.paymentId)
          : undefined,
        onCancel: fetchPayments,
      });
    } catch (e) {
      if (e.response?.status === 403) {
        message.error("You don't have permission to initiate payments");
      } else {
        message.error(
          e.response?.data?.message || "Failed to initiate payment",
        );
      }
    } finally {
      setPayLoading(false);
    }
  };

  // ── Table columns ─────────────────────────────────────────────────────────
  const columns = [
    {
      title: "Payment ID",
      dataIndex: "id",
      key: "id",
      render: (id) => (
        <code
          style={{
            background: "#f0f4ff",
            padding: "2px 8px",
            borderRadius: 4,
            color: "#7B4D12",
            fontSize: 12,
          }}
        >
          {id?.slice(0, 8)}…
        </code>
      ),
    },
    {
      title: "Reservation",
      key: "reservation",
      render: (_, r) => {
        const trip = r.reservation?.tripDate?.trip?.name || "-";
        const date = r.reservation?.tripDate?.date || "-";
        const customer = r.reservation?.user
          ? `${r.reservation.user.firstName} ${r.reservation.user.lastName}`
          : r.reservation?.guestName || "Guest";

        // Check if user can view reservation details
        const canViewReservation = hasPermission(
          userPermissions,
          "reservations.view",
        );

        return (
          <div>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{customer}</div>
            <div style={{ fontSize: 12, color: "#9ca3af" }}>
              {trip} — {date}
            </div>
            {canViewReservation && (
              <button
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                  fontSize: 11,
                  color: "#7B4D12",
                }}
                onClick={() => history.push(`/reservations/${r.reservationId}`)}
              >
                View reservation →
              </button>
            )}
          </div>
        );
      },
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (v) => <strong>EGP {Number(v).toLocaleString()}</strong>,
    },
    {
      title: "Method",
      dataIndex: "method",
      key: "method",
      render: (m) => <Tag>{m?.toUpperCase()}</Tag>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s) => (
        <Tag color={STATUS_COLORS[s]} icon={STATUS_ICONS[s]}>
          {s?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Transaction ID",
      dataIndex: "transactionId",
      key: "txn",
      render: (t) =>
        t ? (
          <code style={{ fontSize: 11, color: "#6b7280" }}>{t}</code>
        ) : (
          <span style={{ color: "#d1d5db" }}>—</span>
        ),
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "date",
      render: (d) => new Date(d).toLocaleString(),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, r) => {
        if (r.status === "pending" && canManagePayments) {
          return (
            <Button
              size="small"
              type="primary"
              style={{ backgroundColor: "#7B4D12", borderColor: "#7B4D12" }}
              icon={<ThunderboltOutlined />}
              loading={simLoading === r.id}
              onClick={() => handleSimulate(r.id)}
            >
              Simulate Success
            </Button>
          );
        }
        return null;
      },
    },
  ];

  // Permission check for viewing the page
  if (!canViewPayments) {
    return (
      <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
        <Header onMenuClick={() => setMenu(!menu)} />
        <Sidebar />
        <div className="page-wrapper">
          <div className="content container-fluid">
            <Alert
              message="Access Denied"
              description="You don't have permission to view payments. Please contact your administrator if you believe this is an error."
              type="error"
              showIcon
            />
          </div>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
      <Header onMenuClick={() => setMenu(!menu)} />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="page-header">
            <div className="content-page-header">
              <h5>Payment Records</h5>
              {canManagePayments && (
                <Button
                  icon={<ReloadOutlined />}
                  onClick={fetchPayments}
                  loading={loading}
                >
                  Refresh
                </Button>
              )}
            </div>
          </div>

          <div className="card mb-0">
            <div className="card-body">
              {/* Filters - Only show if user has manage permissions */}
              {canManagePayments && (
                <div className="row mb-4">
                  <div className="col-md-3">
                    <div className="input-block mb-0">
                      <label
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: "#6b7280",
                          textTransform: "uppercase",
                          letterSpacing: ".04em",
                        }}
                      >
                        Filter by Status
                      </label>
                      <Select
                        style={{ width: "100%" }}
                        placeholder="All statuses"
                        value={filters.status || undefined}
                        onChange={(v) =>
                          setFilters((f) => ({
                            ...f,
                            status: v || undefined,
                            page: 1,
                          }))
                        }
                        allowClear
                      >
                        <Option value="pending">Pending</Option>
                        <Option value="success">Success</Option>
                        <Option value="failed">Failed</Option>
                        <Option value="refunded">Refunded</Option>
                      </Select>
                    </div>
                  </div>
                </div>
              )}

              {/* Stats bar - Only show if user has manage permissions */}
              {canManagePayments && (
                <div
                  style={{
                    display: "flex",
                    gap: 16,
                    marginBottom: 20,
                    flexWrap: "wrap",
                  }}
                >
                  {[
                    { label: "Total", color: "#7B4D12", status: undefined },
                    { label: "Pending", color: "#d97706", status: "pending" },
                    { label: "Confirmed", color: "#059669", status: "success" },
                    { label: "Failed", color: "#dc2626", status: "failed" },
                    { label: "Refunded", color: "#7c3aed", status: "refunded" },
                  ].map((item) => (
                    <button
                      key={item.label}
                      onClick={() =>
                        setFilters((f) => ({
                          ...f,
                          status: item.status,
                          page: 1,
                        }))
                      }
                      style={{
                        background:
                          filters.status === item.status
                            ? `${item.color}15`
                            : "#f8fafc",
                        border: `1.5px solid ${
                          filters.status === item.status
                            ? item.color
                            : "#e5e7eb"
                        }`,
                        borderRadius: 8,
                        padding: "6px 16px",
                        cursor: "pointer",
                        fontSize: 13,
                        fontWeight: filters.status === item.status ? 700 : 400,
                        color:
                          filters.status === item.status
                            ? item.color
                            : "#6b7280",
                        fontFamily: "inherit",
                      }}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              )}

              {/* Show message if user can view but not manage */}
              {!canManagePayments && payments.length > 0 && (
                <Alert
                  message="Limited View"
                  description="You can view payments but cannot filter or manage them. Contact an administrator for full access."
                  type="info"
                  showIcon
                  className="mb-4"
                />
              )}

              <ResponsiveTable
                columns={columns}
                dataSource={payments}
                rowKey="id"
                loading={loading}
                scroll={{ x: true }}
                pagination={{
                  current: filters.page,
                  pageSize: filters.limit,
                  total,
                  showSizeChanger: canManagePayments,
                  pageSizeOptions: ["10", "15", "25", "50"],
                  showTotal: (t, r) => `${r[0]}–${r[1]} of ${t} payments`,
                  onChange: (page, limit) =>
                    setFilters((f) => ({ ...f, page, limit })),
                }}
                locale={{
                  emptyText: canManagePayments
                    ? "No payments found"
                    : "No payments to display",
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Pay Now Modal - Only show if user can create payments ── */}
      {canCreatePayments && (
        <Modal
          title={
            <span>
              <CreditCardOutlined style={{ marginRight: 8 }} />
              Complete Payment
            </span>
          }
          open={payModal.open}
          onOk={handlePayNow}
          onCancel={() => setPayModal({ open: false, reservationId: null })}
          confirmLoading={payLoading}
          okText="Proceed to Payment"
          destroyOnClose
        >
          <Alert
            message="Choose a payment method to confirm your booking."
            type="info"
            showIcon
            className="mb-4"
          />
          <div className="input-block mb-3">
            <label
              style={{
                fontWeight: 600,
                fontSize: 13,
                display: "block",
                marginBottom: 6,
              }}
            >
              Payment Method
            </label>
            <Select
              style={{ width: "100%" }}
              value={payMethod}
              onChange={setPayMethod}
            >
              <Option value="card">💳 Credit / Debit Card</Option>
              <Option value="fawry">🟠 Fawry</Option>
              <Option value="paymob">📱 Paymob</Option>
              <Option value="cash">💵 Cash on Boarding</Option>
            </Select>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default PaymentsPage;
